import { ArrowRight, ChevronLeft } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { Pressable, StyleSheet, Text as RNText, View, type LayoutChangeEvent } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Avatar } from '@/components/Avatar';
import { BACK_CIRCLE_SIZE, goBack } from '@/components/BackChevron';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { DEAL_HERO_OVERLAP } from '@/components/DealDetailHero';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { usePushOnce } from '@/hooks/usePushOnce';
import { settledCard } from '@/lib/socialProgress';
import { uploadPhaseFor } from '@/stores/voltrUploadPhase';
import type { Action, Deal, DealCta, DealStep } from '@/types/deal';

import { heroAttention, heroCtaStep, heroUploadAsks, heroWaitingLabel } from './socialHeroState';
import { SocialHeroStatus } from './SocialHeroStatus';

export type SocialDealHeroProps = {
  deal: Deal;
  action?: Action;
  onCtaPress: (step: DealStep, cta?: DealCta) => void;
  ctaLoading?: boolean;
  onLayout?: (e: LayoutChangeEvent) => void;
  /** "Fix 1 video": scrolls the page to that video's card. */
  onFixVideo?: (videoId: string) => void;
};

/**
 * MarketPryce Social deal header (Figma "Apply" frame): back, the brand's
 * tile and name, the server's headline (`marketing_copy.social.headline`)
 * and the deal's description under it,
 * and the next step's button so the ask is above the fold. As the Figma's
 * Create Deal Detail frames have it: "Track package" while the package is on
 * its way (5), "Submit" once videos are picked (9), an "Uploading content"
 * pill while they send (8). With no button:
 * a finished deal's card ("Approved", "This one didn't go through"), or the
 * server's word for a wait ("Reviewing application"). Full-bleed Deep
 * Green under the status bar; the content sheet rides up over its bottom
 * edge (DEAL_HERO_OVERLAP), same as every deal page.
 */
export const SocialDealHero = observer(function SocialDealHero({ deal, action, onCtaPress, ctaLoading, onLayout, onFixVideo }: SocialDealHeroProps) {
  const pushOnce = usePushOnce();
  const upload = action ? uploadPhaseFor(action.id) : null;
  const { uploadStep, uploadCta, uploading, submit, trackable } = heroUploadAsks(deal, action, upload);
  const headline = deal.marketing_copy?.social?.headline ?? deal.title;
  const ctaStep = heroCtaStep(deal, action);
  const cta = ctaStep?.ctas?.find((c) => c.kind === 'primary');
  // A video a coach sent back outranks every other ask, whichever step the
  // server opened first (the playbook can be open beside it): "Fix 1 video" jumps to it.
  // A finished deal says how it ended, whatever a video still carries.
  const settled = settledCard(action);
  // Picked videos speak first (they're what the athlete just did), then a sent-back video, then a package on its way.
  const fix = settled || upload ? undefined : heroAttention(deal, action);
  const track = trackable && !fix;
  const entry = ctaStep ? action?.step_states?.[ctaStep.key] : undefined;
  const ctaLabel = entry?.add_label ?? cta?.label;
  const waiting = uploading ? UPLOADING : ctaStep || fix || settled || submit || track ? undefined : heroWaitingLabel(deal, action);

  return (
    <SafeAreaView edges={['top']} style={styles.hero} onLayout={onLayout} testID="social-hero">
      <RNText
        style={styles.watermark}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        M
      </RNText>
      {/* Opened from a link or a push there is no history: land on Create (where social deals live), not Home. */}
      <Pressable
        onPress={() => goBack('/create')}
        hitSlop={12}
        accessibilityRole="button"
        accessibilityLabel="Back"
        style={({ pressed }) => [styles.back, pressed && styles.pressed]}
      >
        <ChevronLeft size={20} color={colors.frost} strokeWidth={2.25} />
        <Text color="frost">Create</Text>
      </Pressable>

      <View style={styles.brandRow}>
        <BrandLogo
          url={deal.brand_logo_url}
          size={BRAND_TILE}
          radius={radii.base}
          inset={6}
          fallback={<Avatar initials={deal.brand_name.slice(0, 2).toUpperCase()} size={BRAND_TILE} tone="frost" />}
        />
        <Text variant="bodyBold" color="frost" numberOfLines={1} style={styles.brandName}>
          {deal.display_name}
        </Text>
      </View>
      <View style={styles.divider} />

      <Heading size="3xl" tone="inverse">
        {headline}
      </Heading>
      {deal.description ? (
        <Text color="frost" testID="social-hero-description">
          {deal.description}
        </Text>
      ) : null}
      {submit ? (
        <Button label="Submit" variant="primary" fullWidth trailingIcon={ArrowRight} onPress={() => uploadStep && onCtaPress(uploadStep, uploadCta)} testID="social-hero-cta" />
      ) : uploading ? null : fix && onFixVideo ? (
        <Button
          label={fix.label}
          variant="primary"
          fullWidth
          trailingIcon={ArrowRight}
          onPress={() => onFixVideo(fix.video_id)}
          testID="social-hero-cta"
        />
      ) : track ? (
        <Button
          label="Track package"
          variant="primary"
          fullWidth
          trailingIcon={ArrowRight}
          onPress={() => pushOnce({ pathname: '/deals/track/[slug]', params: { slug: deal.slug } })}
          testID="social-hero-cta"
        />
      ) : ctaStep && cta ? (
        <Button
          label={ctaLabel ?? cta.label}
          variant="primary"
          fullWidth
          trailingIcon={ArrowRight}
          loading={ctaLoading}
          onPress={() => onCtaPress(ctaStep, cta)}
          testID="social-hero-cta"
        />
      ) : null}
      <SocialHeroStatus settled={settled} waiting={waiting} />
    </SafeAreaView>
  );
});

const BRAND_TILE = 52;

/** Figma "Create Deal Detail 8": the header's pill while picked videos send. */
const UPLOADING = 'Uploading content';

const styles = StyleSheet.create({
  hero: {
    backgroundColor: colors.deepGreen,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg + DEAL_HERO_OVERLAP,
    gap: spacing.base,
    overflow: 'hidden',
  },
  // Same page-16 watermark as DealDetailHero: Frost @ 8%, bleeding off the top-right.
  watermark: {
    position: 'absolute',
    top: -8,
    right: -36,
    fontFamily: fonts.numeric,
    fontSize: fontSizes.watermark,
    lineHeight: fontSizes.watermark,
    color: colors.watermarkOnDark,
    transform: [{ rotate: '-8deg' }],
    letterSpacing: -10,
  },
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    marginLeft: -spacing.xs,
    // A full 44pt touch target for a 22pt line of text.
    minHeight: BACK_CIRCLE_SIZE,
  },
  pressed: {
    opacity: 0.6,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  // Brand name and headline are set as on DealDetailHero (body bold, then the
  // app's Heading), not the Figma's wide face: type follows the app for now.
  brandName: {
    flex: 1,
    fontSize: fontSizes.lg,
  },
  divider: {
    height: 1,
    backgroundColor: colors.frostTint,
  },
});
